"use client"

import { motion } from "framer-motion"
import { ArrowRight } from "lucide-react"
import HeroHeading from "./hero/hero-heading"
import { HERO_BADGES } from "./hero/hero-data"
import { useTranslations } from "next-intl"
import AnimatedButton from "./ui/animated-button"

export default function Hero() {
  const t = useTranslations("hero")
  const scrollToContact = () => {
    const contactSection = document.getElementById("contact")
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }
  return (
    <section className="relative min-h-screen flex items-center pt-16 pb-16 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 to-black" />
        <div className="absolute top-0 left-0 right-0 h-[500px] bg-gradient-to-b from-purple-900/10 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(120,50,255,0.1),transparent_65%)]" />
      </div>
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="max-w-5xl mx-auto text-center lg:text-left">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-10"
          >
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 border border-white/30 rounded-full text-sm text-white font-medium backdrop-blur-sm"
              >
                <div className="w-2 h-2 bg-green-400 rounded-full mr-3 animate-pulse"></div>
                <span>{t("badge")}</span>
              </motion.div>
              <HeroHeading />
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="text-lg sm:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto lg:mx-0"
              >
                {t("description")}
              </motion.p>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex flex-col gap-8 items-center justify-center lg:justify-start lg:items-start"
            >
              <AnimatedButton
                variant="slim"
                className="bg-white text-black hover:bg-gray-100 text-sm px-6 py-2.5"
                onClick={scrollToContact}
              >
                <span className="flex items-center">
                  {t("cta")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </span>
              </AnimatedButton>
              <div className="flex flex-col sm:flex-row sm:flex-wrap items-center gap-4 sm:gap-8">
                {HERO_BADGES.map(({ key, icon: Icon, color }) => <div key={key} className="flex items-center space-x-3">
                  <div className={`w-10 h-10 shrink-0 ${color} rounded-xl flex items-center justify-center shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-white">{t(`badges.${key}.name`)}</p>
                    <p className="text-xs text-gray-400">{t(`badges.${key}.description`)}</p>
                  </div>
                </div>)}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
